import { createContext, useContext } from 'react';

/**
 * #885: what the public pages need to know about the visitor. Signed out, gated actions open a
 * "Sign in to …" prompt; `signIn` returns to the page it was asked from.
 */
export interface PublicAccess {
  readonly signedIn: boolean;
  readonly signInError?: string | null;
  readonly signIn: (returnTo: string) => void;
  /** #889: create an account, then return to `returnTo`. */
  readonly signUp: (returnTo: string) => void;
}

export const PublicAccessContext = createContext<PublicAccess>({
  signedIn: false,
  signIn: () => {},
  signUp: () => {}
});

export function usePublicAccess(): PublicAccess {
  return useContext(PublicAccessContext);
}

/** #1124 (ADR 0023): a public reaction's catalogue address -- ADR 0006's `/r/<id>`. */
export function publicReactionPath(reactionId: string): string {
  return `/r/${encodeURIComponent(reactionId)}`;
}

/** #1123 (ADR 0023): a public drawing's catalogue address -- ADR 0006's illustration path, `/i/<id>`. */
export function publicDrawingPath(drawingId: string): string {
  return `/i/${encodeURIComponent(drawingId)}`;
}

/**
 * ADR 0017 D10 (#904): a Molecule's page. Signed out it's the public address; signed in, the
 * object page, which also opens a structure only private drawings hold (D8).
 */
export function moleculePagePath(moleculeId: string, signedIn: boolean): string {
  return signedIn ? `/objects/molecule/${encodeURIComponent(moleculeId)}` : catalogueMoleculePath(moleculeId);
}

/** ADR 0023 D3 (#1113): a molecule's public address in the catalogue, ADR 0006's `/m/<id>`. */
export function catalogueMoleculePath(moleculeId: string): string {
  return `/m/${encodeURIComponent(moleculeId)}`;
}

/** ADR 0023 D3 (owner, 2026-10-29: the apex from day one): where search engines are told a page lives. */
export const CATALOGUE_ORIGIN = 'https://peruselab.com';
